import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  EntryPass,
  EntryPassEntryTimeSlot,
  EntryPassLocationSpecificPricingMatrix,
  getEntryPassStatusDisplay,
} from '../../../../../../models/entry-pass';
import { TaxRate } from '../../../../../../models/tax-rate';
import { AccountService } from '../../../../../../services/account/account.service';
import { EntryPassService } from '../../../../../../services/entry-pass/entry-pass.service';
import { TaxRateService } from '../../../../../../services/tax-rate/tax-rate.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { MyAccount } from '../../../../../../models/my-account';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { EntryPassStatus, EntryPassVisibility } from '../../../../../../models/entry-pass-enums';
import { deleteProp, getRandomNegativeId, scrollToElement } from '../../../../../../utils';
import { CapitanLocation } from '../../../../../../models/location';
import { LocationService } from '../../../../../../services/location/location.service';
import {
  EditEntryPassPerLocationPricingMatrixModalComponent,
} from './modals/edit-entry-pass-per-location-pricing-matrix-modal/edit-entry-pass-per-location-pricing-matrix-modal.component';
import { SelectLocationModalComponent } from '../../../modals/select-location-modal/select-location-modal.component';
import { MembershipVisibility } from '../../../../../../models/membership-enums';

@Component({
  selector: 'app-entry-pass-detail',
  templateUrl: './entry-pass-detail.component.html',
  styleUrls: ['./entry-pass-detail.component.css']
})
export class EntryPassDetailComponent implements OnInit, ComponentHasUnsavedChanges {

  public isNewEntryPass: boolean;
  public entryPass: EntryPass = {
    id: null,
    url: null,
    name: '',
    status: EntryPassStatus.draft,
    description: '',
    confirmation_text: '',
    payment_information_text: '',
    terms_and_conditions_checkbox_text: '',
    visibility: EntryPassVisibility.public,
    num_passes: null,
    price: null,
    location_specific_pricing_matrices: [],
    tax_rate_id: null,
    tax_rate_name: null,
    tax_rate_per_location: {},
    expiration_months: null,
    expiration_days: null,
    revenue_account_codes: {},
    allow_purchasing_for_unlinked_customers: true,
    allow_purchase_without_login: true,
    has_limited_entry: false,
    mailchimp_last_entry_used_event_name: '',
    mailchimp_unused_passes_tag_name: '',
    mailchimp_purchased_by_customer_event_name: '',
    mailchimp_purchased_for_customer_event_name: '',
    order_number: 0,
    created_at: null,
    updated_at: null,
    last_updated_by_id: null,
    last_updated_by_first_name: null,
    last_updated_by_last_name: null,
    last_updated_by_email: null,
    entry_time_slots_url: null
  };
  public entryTimeSlots: EntryPassEntryTimeSlot[] = [];
  public entryTimeSlotUrlsToDelete: string[] = [];

  // If this is an existing pass, store the existing status to determine
  // whether changing "Draft-edit-only" fields is allowed. We cannot simply
  // read the status of the pass object because then any restricted actions
  // would take effect as soon as the status is changed locally, rather than
  // upon saving.
  public existingStatus: EntryPassStatus;
  public hasPerLocationTaxRates = false;

  // The id of the section currently scrolled into view, used to highlight the
  // active link in the in-page navigation. Updated by onSectionChange().
  public currentSectionId = 'setup';

  public errorMessage = '';
  public errors: any = {};
  public entryTimeSlotErrors: {[key: number]: any} = {};
  public entryPassNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public taxRateList: TaxRate[];

  public loadingExistingEntryPass = false;
  public loadingExistingEntryTimeSlots = false;
  public loadingSubmit = false;
  public loadingTaxRates = true;

  public myAccount: MyAccount;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly getEntryPassStatusDisplay = getEntryPassStatusDisplay;
  public readonly deleteProp = deleteProp;
  public readonly scrollToElement = scrollToElement;
  public readonly EntryPassVisibility = EntryPassVisibility;
  public readonly Object = Object;

  // Accessed via getLocations()
  private locations: CapitanLocation[] = null;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private locationService: LocationService,
    private entryPassService: EntryPassService,
    private taxRateService: TaxRateService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewEntryPass = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Either get the pass to edit (if an ID was provided) or construct an
    // empty pass for creation.
    if (!this.isNewEntryPass) {
      const entryPassIdInt = parseInt(routeParams.id, 10);
      if (isNaN(entryPassIdInt)) {
        this.entryPassNotFound = true;
        this.loadingExistingEntryPass = false;
        return;
      }

      this.loadEntryPass(entryPassIdInt);
      this.loadEntryTimeSlots(entryPassIdInt);
    }

    this.loadTaxRates();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the entry time slots for the entry pass with the given ID (used for
   * editing existing entry passes).
   */
  public loadEntryTimeSlots(entryPassId: number) {
    this.loadingExistingEntryTimeSlots = true;

    this.entryPassService
      .getEntryTimeSlots(entryPassId, 1, 100)
      .then(entryTimeSlotsResponse => {
        this.entryTimeSlots = entryTimeSlotsResponse.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.entryPassNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingEntryTimeSlots = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRateList = response.results;

        // Set the initial tax rate to the first one in the list, if the entry
        // pass does not already have a tax rate. If this loads before the entry
        // pass does, that's okay because the entry pass will be overwritten.
        if (this.entryPass.tax_rate_id === null) {
          if (this.taxRateList.length > 0) {
            this.entryPass.tax_rate_id = this.taxRateList[0].id;
          }
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  /**
   * Get the list of available statuses for the entry pass. This enforces the
   * rule that an entry pass may not be reverted to Draft once it has become
   * Active or Retired.
   */
  public getAvailableStatuses(): EntryPassStatus[] {
    if (this.isExistingNonDraftEntryPass()) {
      return [EntryPassStatus.active, EntryPassStatus.retired];
    } else {
      return [EntryPassStatus.draft, EntryPassStatus.active, EntryPassStatus.retired];
    }
  }

  /**
   * Whether the entry pass is an existing, non-draft pass.
   * This determines whether certain basic fields can be edited.
   */
  public isExistingNonDraftEntryPass(): boolean {
    return !this.isNewEntryPass && (this.existingStatus !== EntryPassStatus.draft);
  }

  public onSectionChange(sectionId: string) {
    this.currentSectionId = sectionId;
  }

  public onSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};
    this.entryTimeSlotErrors = {};

    if (!this.hasPerLocationTaxRates) {
      this.entryPass.tax_rate_per_location = {};
    }

    // The promise is different between creating and updating, however the
    // post-resolution actions are the same.
    let submitPromise: Promise<EntryPass>;
    if (this.isNewEntryPass) {
      submitPromise = this.entryPassService.addEntryPass(this.entryPass);
    } else {
      submitPromise = this.entryPassService.updateEntryPass(this.entryPass);
    }

    submitPromise
      .then(entryPass => {
        this.entryPass = entryPass;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        }

        return Promise.reject(reason);
      })
      .then(() => {
        return this.saveTimeSlots();
      })
      .then(() => {
        this.unsavedChanges = false;

        let successMessage: string;
        if (this.isNewEntryPass) {
          successMessage = this.entryPass.name + ' created successfully.';
        } else {
          successMessage = this.entryPass.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/memberships-and-passes'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          // Do nothing: this is handled in the individual promises.
        } else if (reason.status === 403) {
          if (this.isNewEntryPass) {
            this.errorMessage = 'You do not have permission to add passes.';
          } else {
            this.errorMessage = 'You do not have permission to modify this pass.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public removeEntryTimeSlot(timeSlot: EntryPassEntryTimeSlot) {
    // If the entry time slot has a positive ID, then it is present in the API
    // and must be deleted.
    if (timeSlot.id > 0) {
      this.entryTimeSlotUrlsToDelete.push(timeSlot.url);
    }

    this.entryTimeSlots.splice(
      this.entryTimeSlots.indexOf(timeSlot),
      1
    );
  }

  /**
   * Whether all days are available for the given time slot.
   */
  public allDaysAreAvailable(timeSlot: EntryPassEntryTimeSlot) {
    return (
      timeSlot.is_available_sunday &&
      timeSlot.is_available_monday &&
      timeSlot.is_available_tuesday &&
      timeSlot.is_available_wednesday &&
      timeSlot.is_available_thursday &&
      timeSlot.is_available_friday &&
      timeSlot.is_available_saturday
    );
  }

  /**
   * Set all days to be available or unavailable for the given time slot.
   */
  public setAllDaysAvailability(timeSlot: EntryPassEntryTimeSlot, isAvailable: boolean) {
    timeSlot.is_available_sunday = isAvailable;
    timeSlot.is_available_monday = isAvailable;
    timeSlot.is_available_tuesday = isAvailable;
    timeSlot.is_available_wednesday = isAvailable;
    timeSlot.is_available_thursday = isAvailable;
    timeSlot.is_available_friday = isAvailable;
    timeSlot.is_available_saturday = isAvailable;
  }

  /**
   * Add a new entry time slot for the entry pass.
   */
  public addNewEntryTimeSlot() {
    this.entryTimeSlots.push({
      id: getRandomNegativeId(),
      url: '',
      start_time: '',
      end_time: '',
      is_available_sunday: false,
      is_available_monday: false,
      is_available_tuesday: false,
      is_available_wednesday: false,
      is_available_thursday: false,
      is_available_friday: false,
      is_available_saturday: false,
      created_at: '',
      updated_at: ''
    });
  }

  public taxRateRemoved(taxRateId: number) {
    if (!this.taxRateList) { return true; }

    for (const taxRate of this.taxRateList) {
      if (taxRate.id === taxRateId) { return false; }
    }

    return true;
  }

  public getLocations(): CapitanLocation[] {
    if (!this.locations) {
      this.locations = this.locationService.getLocations();
    }
    return this.locations;
  }

  public getLocNameById(locId: string | number) {
    const locations = this.getLocations();

    if (locations === null) {
      return 'Loading...';
    }

    for (const loc of locations) {
      if (loc.id.toString() === locId.toString()) { return loc.name; }
    }

    return 'Unknown Location';
  }

  public editPricingMatrix(matIdx: number) {
    if (!this.getLocations()) { return; }

    const modalRef = this.modalService.open(
      EditEntryPassPerLocationPricingMatrixModalComponent
    );
    modalRef.componentInstance.inputPricingMatrix = this.entryPass.location_specific_pricing_matrices[matIdx];
    modalRef.componentInstance.entryPass = this.entryPass;
    modalRef.componentInstance.locations = this.getLocations();
    modalRef.componentInstance.completedCallback = (result: EntryPassLocationSpecificPricingMatrix) => {
      this.unsavedChanges = true;
      this.removeLocationIdsFromExistingPricingMatrices(result.location_ids);
      this.entryPass.location_specific_pricing_matrices[matIdx] = result;
    };
  }

  public addPricingMatrix() {
    if (!this.getLocations()) { return; }

    const modalRef = this.modalService.open(
      EditEntryPassPerLocationPricingMatrixModalComponent
    );
    modalRef.componentInstance.entryPass = this.entryPass;
    modalRef.componentInstance.locations = this.getLocations();
    modalRef.componentInstance.completedCallback = (result: EntryPassLocationSpecificPricingMatrix) => {
      this.unsavedChanges = true;
      this.removeLocationIdsFromExistingPricingMatrices(result.location_ids);
      this.entryPass.location_specific_pricing_matrices.push(result);
    };
  }

  public removePricingMatrix(matIdx: number) {
    const isConfirmed = confirm(
      'Are you sure you want to remove this per-location price?'
    );
    if (isConfirmed) {
      this.unsavedChanges = true;
      this.entryPass.location_specific_pricing_matrices.splice(matIdx, 1);
    }
  }

  public addTaxRateLocation() {
    if (this.loadingTaxRates) {
      return;
    }

    const modalRef = this.modalService.open(SelectLocationModalComponent);
    modalRef.componentInstance.excludeIds = Object
      .keys(this.entryPass.tax_rate_per_location)
      .map(locId => parseInt(locId, 10));
    modalRef.componentInstance.submitCallback = (result: CapitanLocation) => {
      this.entryPass.tax_rate_per_location[result.id.toString()] = this.taxRateList[0].id;
      this.unsavedChanges = true;
    }
  }

  /**
   * Load the entry pass with the given ID (used for editing existing passes).
   */
  private loadEntryPass(entryPassId: number) {
    this.loadingExistingEntryPass = true;

    this.entryPassService
      .getEntryPass(entryPassId)
      .then(entryPass => {
        this.entryPass = entryPass;
        this.existingStatus = entryPass.status;
        if (Object.keys(this.entryPass.tax_rate_per_location).length > 0) {
          this.hasPerLocationTaxRates = true;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.entryPassNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingEntryPass = false; });
  }

  private saveTimeSlots(): Promise<any>[] {
    const savePromises: Promise<any>[] = [];

    // Save the entry pass time slots.
    for (const timeSlotToSave of this.entryTimeSlots) {
      let timeSlotSavePromise: Promise<EntryPassEntryTimeSlot>;
      if (timeSlotToSave.id > 0) {
        timeSlotSavePromise = this.entryPassService.updateEntryTimeSlot(timeSlotToSave);
      } else {
        timeSlotSavePromise = this.entryPassService.addEntryTimeSlot(this.entryPass.id, timeSlotToSave);
      }
      timeSlotSavePromise = timeSlotSavePromise.catch(reason => {
        if (reason.status === 400) {
          this.entryTimeSlotErrors[timeSlotToSave.id] = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        }

        return Promise.reject(reason);
      });
      savePromises.push(timeSlotSavePromise);
    }

    // Delete the removed entry pass time slots.
    for (const timeSlotUrlToDelete of this.entryTimeSlotUrlsToDelete) {
      savePromises.push(
        this.entryPassService.deleteEntryTimeSlot(timeSlotUrlToDelete)
      );
    }

    return savePromises;
  }

  private removeLocationIdsFromExistingPricingMatrices(locIds: number[]) {
    for (const thisMatrix of this.entryPass.location_specific_pricing_matrices) {
      thisMatrix.location_ids = thisMatrix.location_ids
        .filter(locId => !locIds.includes(locId));
      }
  }

  protected readonly MembershipVisibility = MembershipVisibility;
}
